(function () {
  "use strict";

  const collectionButtonSelector = ".swym-advanced-wishlist-collections";
  const productButtonSelector =
    ".swym-custom-pdp-button .swym-button-content button, .product-atc-wrap .swym-button-content.swym-btn-custom-position button";
  const buttonSelector = `${collectionButtonSelector}, ${productButtonSelector}`;
  const productLinkSelector = 'a.product-item__image-wrapper[href*="/products/"]';
  const script = document.currentScript;
  const addLabel = script?.dataset.addLabel || "Add to wishlist";
  const removeLabel = script?.dataset.removeLabel || "Remove from wishlist";
  const addedMessage = script?.dataset.addedMessage || "Added to wishlist";
  const removedMessage = script?.dataset.removedMessage || "Removed from wishlist";
  const errorMessage = script?.dataset.errorMessage || "Unable to update wishlist";
  const toggleStates = new Map();
  let swatInstance = null;

  function productHandle(url) {
    if (!url) return "";

    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return pathname.match(/\/products\/([^/]+)/)?.[1] || "";
    } catch (error) {
      return "";
    }
  }

  function buttonProductHandle(button) {
    const card = button.closest("product-item");
    if (card) return productHandle(card.querySelector(productLinkSelector)?.href);

    return productHandle(window.location.href);
  }

  function buttonVariantId(button) {
    if (button.matches(collectionButtonSelector)) {
      const card = button.closest("product-item");
      if (card?.dataset.swymVariantValid === "false") return null;

      const quickBuyVariant = card?.querySelector(
        'quick-buy .cart-add__form select[name="id"]',
      )?.value;

      return quickBuyVariant || card?.dataset.selectedVariantId || null;
    }

    const productForm = button.closest("product-form");
    if (productForm?.dataset.swymVariantValid === "false") return null;

    return productForm?.querySelector(".product-form__master-select")?.value || null;
  }

  function productFormHasMatchingVariant(productForm) {
    const form = productForm.querySelector(".main-product__form");
    const masterSelect = productForm.querySelector(".product-form__master-select");
    if (!form || !masterSelect) return false;

    const selectedOptions = [];
    new FormData(form).forEach((value, key) => {
      if (key.includes("option")) selectedOptions.push(value);
    });
    const selectedVariantOptions = selectedOptions.join("||");

    return Array.from(masterSelect.options).some(
      (option) => option.dataset.variantOptions === selectedVariantOptions,
    );
  }

  function buttonProductUrl(button) {
    const card = button.closest("product-item");
    const productUrl = card?.querySelector(productLinkSelector)?.href || window.location.href;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.search = "";
      url.hash = "";
      return url.toString();
    } catch (error) {
      return productUrl;
    }
  }

  function buttonProduct(button) {
    const card = button.closest("product-item");
    const image = card?.querySelector(".product-card__image.media--primary img");

    return {
      handle: buttonProductHandle(button),
      productId: card?.dataset.productId || window.SwymPageData?.empi || null,
      variantId: buttonVariantId(button),
      url: buttonProductUrl(button),
      source: card ? "collections-grid" : "pdp",
      title:
        card?.querySelector(".product-item__title")?.textContent.trim() ||
        window.SwymPageData?.dt ||
        "",
      imageUrl: image?.currentSrc || image?.src || window.SwymPageData?.iu || "",
    };
  }

  function productKey(product) {
    return `${product.handle}:${product.variantId || "all"}`;
  }

  function itemMatchesProduct(item, product) {
    if (product.productId && String(item?.empi) !== String(product.productId)) return false;
    if (!product.productId && productHandle(item?.du) !== product.handle) return false;
    if (!product.variantId) return true;

    return String(item.epi) === String(product.variantId);
  }

  function productItem(product) {
    return {
      epi: product.variantId,
      empi: product.productId,
      du: product.url,
      source: product.source,
      dt: product.title,
      iu: product.imageUrl,
    };
  }

  function productPayload(item) {
    const product = {
      epi: item.epi,
      empi: item.empi,
      du: item.du,
    };
    const optionalFields = [
      "qty",
      "note",
      "cprops",
      "lbls",
      "_av",
      "source",
      "dt",
      "iu",
      "pr",
      "stk",
      "vi",
      "bt",
    ];

    optionalFields.forEach((field) => {
      if (item[field] !== undefined && item[field] !== null) product[field] = item[field];
    });

    return product;
  }

  function fetchDefaultWishlist(swat) {
    return new Promise((resolve) => swat.fetch(resolve));
  }

  function removeFromDefaultWishlist(swat, item) {
    return new Promise((resolve, reject) => {
      swat.removeFromWishList(
        productPayload(item),
        () => resolve({ item, listId: null }),
        reject,
      );
    });
  }

  function addToDefaultWishlist(swat, item) {
    return new Promise((resolve, reject) => {
      swat.addToWishList(
        productPayload(item),
        (addedItem) => resolve({ item: { ...item, ...addedItem }, listId: null }),
        reject,
      );
    });
  }

  function fetchLists(swat) {
    return new Promise((resolve, reject) => {
      swat.fetchLists({ callbackFn: resolve, errorFn: reject });
    });
  }

  function createWishlist(swat) {
    return new Promise((resolve, reject) => {
      swat.createList({ lname: "My Wishlist" }, resolve, reject);
    });
  }

  function fetchListContents(swat, list) {
    if (typeof swat.fetchListCtx !== "function") {
      return Promise.resolve(list.listcontents || []);
    }

    return new Promise((resolve) => {
      swat.fetchListCtx({ lid: list.lid }, resolve, () => resolve(list.listcontents || []));
    });
  }

  function deleteFromList(swat, listId, item) {
    return new Promise((resolve, reject) => {
      swat.deleteFromList(listId, productPayload(item), () => resolve({ item, listId }), reject);
    });
  }

  function addToList(swat, listId, item) {
    return new Promise((resolve, reject) => {
      swat.addToList(
        listId,
        productPayload(item),
        (addedItem) => resolve({ item: { ...item, ...addedItem }, listId }),
        reject,
      );
    });
  }

  async function addNewWishlistItem(swat, item) {
    if (typeof swat.fetchLists !== "function" || typeof swat.addToList !== "function") {
      return addToDefaultWishlist(swat, item);
    }

    const lists = await fetchLists(swat);
    const wishlist = lists.find((list) => !list.lty || list.lty === "wl") || lists[0];
    if (wishlist?.lid) return addToList(swat, wishlist.lid, item);

    if (typeof swat.createList !== "function") return addToDefaultWishlist(swat, item);

    const createdWishlist = await createWishlist(swat);
    if (!createdWishlist?.lid) return addToDefaultWishlist(swat, item);

    return addToList(swat, createdWishlist.lid, item);
  }

  async function removeFromEveryList(swat, product) {
    const lists = await fetchLists(swat);
    const listContents = await Promise.all(
      lists.map(async (list) => ({ list, items: await fetchListContents(swat, list) })),
    );
    const removals = listContents.flatMap(({ list, items }) =>
      items
        .filter((item) => itemMatchesProduct(item, product))
        .map((item) => deleteFromList(swat, list.lid, item)),
    );

    return Promise.all(removals);
  }

  async function removeFromWishlist(swat, product) {
    const collectionsEnabled =
      typeof swat.isCollectionsEnabled === "function" && swat.isCollectionsEnabled();

    if (
      collectionsEnabled &&
      typeof swat.fetchLists === "function" &&
      typeof swat.deleteFromList === "function"
    ) {
      const removals = await removeFromEveryList(swat, product);
      if (removals.length) return removals;
    }

    const items = await fetchDefaultWishlist(swat);
    return Promise.all(
      items
        .filter((item) => itemMatchesProduct(item, product))
        .map((item) => removeFromDefaultWishlist(swat, item)),
    );
  }

  function restoreToWishlist(swat, memberships) {
    return Promise.all(
      memberships.map(({ item, listId }) => {
        if (listId && typeof swat.addToList === "function") {
          return addToList(swat, listId, item);
        }

        return addToDefaultWishlist(swat, item);
      }),
    );
  }

  function removeMemberships(swat, memberships) {
    return Promise.all(
      memberships.map(({ item, listId }) => {
        if (listId && typeof swat.deleteFromList === "function") {
          return deleteFromList(swat, listId, item);
        }

        return removeFromDefaultWishlist(swat, item);
      }),
    );
  }

  async function fetchWishlistMemberships(swat) {
    const collectionsEnabled =
      typeof swat.isCollectionsEnabled === "function" && swat.isCollectionsEnabled();

    if (collectionsEnabled && typeof swat.fetchLists === "function") {
      const lists = await fetchLists(swat);
      if (lists.length) {
        const listContents = await Promise.all(
          lists.map(async (list) => ({ list, items: await fetchListContents(swat, list) })),
        );

        return listContents.flatMap(({ list, items }) =>
          items.map((item) => ({ item, listId: list.lid })),
        );
      }
    }

    const items = await fetchDefaultWishlist(swat);
    return items.map((item) => ({ item, listId: null }));
  }

  async function loadWishlistState(swat) {
    const memberships = await fetchWishlistMemberships(swat);

    memberships.forEach((membership) => {
      const item = membership.item;
      const key = productKey({
        handle: productHandle(item.du),
        variantId: item.epi,
      });
      const state = toggleStates.get(key) || { isAdded: true, memberships: [] };
      state.isAdded = true;
      state.memberships.push(membership);
      toggleStates.set(key, state);
    });

    document.querySelectorAll(buttonSelector).forEach((button) => {
      refreshButtonState(button);
    });
  }

  function showNotification(swat, message) {
    if (typeof swat.ui?.showSuccessNotification !== "function") return;

    swat.ui.showSuccessNotification({ message });
  }

  function showErrorNotification(swat) {
    if (typeof swat.ui?.showErrorNotification !== "function") return;

    swat.ui.showErrorNotification({ message: errorMessage });
  }

  function showRemovedState(product) {
    document.querySelectorAll(buttonSelector).forEach((button) => {
      const candidate = buttonProduct(button);
      if (candidate.handle !== product.handle) return;
      if (product.variantId && candidate.variantId !== product.variantId) return;

      button.setAttribute("aria-pressed", "false");
      button.setAttribute("aria-label", addLabel);
      button.querySelectorAll("svg path").forEach((path) => path.setAttribute("fill", "none"));
    });
  }

  function showAddedState(product) {
    document.querySelectorAll(buttonSelector).forEach((button) => {
      const candidate = buttonProduct(button);
      if (candidate.handle !== product.handle) return;
      if (product.variantId && candidate.variantId !== product.variantId) return;

      button.setAttribute("aria-pressed", "true");
      button.setAttribute("aria-label", removeLabel);
      button
        .querySelectorAll("svg path")
        .forEach((path) => path.setAttribute("fill", "currentColor"));
    });
  }

  function refreshRememberedState(product) {
    const state = toggleStates.get(productKey(product));
    if (!state) {
      showRemovedState(product);
      return;
    }

    if (state.isAdded) {
      showAddedState(product);
    } else {
      showRemovedState(product);
    }
  }

  function setButtonVisibility(button, isVisible) {
    button.hidden = !isVisible;
    const container = button.matches(collectionButtonSelector)
      ? button.closest(".swym-advanced-wishlist-collections-container")
      : button.closest(".swym-button-content");
    if (container) container.hidden = !isVisible;
  }

  function refreshButtonState(button) {
    const product = buttonProduct(button);
    const hasVariant = Boolean(product.variantId);

    setButtonVisibility(button, hasVariant);
    if (!hasVariant) return;

    refreshRememberedState(product);
  }

  function scheduleStateRefresh(product) {
    window.setTimeout(() => refreshRememberedState(product), 0);
    window.setTimeout(() => refreshRememberedState(product), 250);
  }

  function scheduleButtonStateRefresh(button) {
    window.setTimeout(() => refreshButtonState(button), 0);
    window.setTimeout(() => refreshButtonState(button), 250);
  }

  async function handleClick(event) {
    const button = event.target.closest(buttonSelector);
    if (!button || !swatInstance) return;

    const product = buttonProduct(button);
    if (!product.handle || !product.variantId || button.getAttribute("aria-busy") === "true") {
      return;
    }
    const key = productKey(product);
    const rememberedState = toggleStates.get(key);
    const isAdded = rememberedState
      ? rememberedState.isAdded
      : button.getAttribute("aria-pressed") === "true";

    event.preventDefault();
    event.stopImmediatePropagation();
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      if (isAdded) {
        const memberships = rememberedState?.memberships?.length
          ? await removeMemberships(swatInstance, rememberedState.memberships)
          : await removeFromWishlist(swatInstance, product);
        if (!memberships.length) throw new Error("No matching SWYM wishlist item was found");

        toggleStates.set(key, { isAdded: false, memberships });
        showRemovedState(product);
        showNotification(swatInstance, removedMessage);
      } else {
        let memberships;
        if (rememberedState) {
          memberships = await restoreToWishlist(swatInstance, rememberedState.memberships);
        } else {
          memberships = [await addNewWishlistItem(swatInstance, productItem(product))];
        }
        toggleStates.set(key, { isAdded: true, memberships });
        showAddedState(product);
        showNotification(swatInstance, addedMessage);
      }

      scheduleStateRefresh(product);
    } catch (error) {
      console.error("Unable to update product in SWYM wishlist", error);
      showErrorNotification(swatInstance);
    } finally {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => {
    swatInstance = swat;
    loadWishlistState(swat).catch((error) => {
      console.error("Unable to load SWYM wishlist state", error);
    });
    window.setTimeout(() => {
      document.querySelectorAll(buttonSelector).forEach((button) => {
        refreshButtonState(button);
      });
    }, 500);
  });
  const buttonObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        const buttons = node.matches(buttonSelector)
          ? [node]
          : Array.from(node.querySelectorAll(buttonSelector));
        buttons.forEach((button) => refreshButtonState(button));
      });
    });
  });
  buttonObserver.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener("product-card:swatch-change", (event) => {
    event.target.dataset.swymVariantValid = String(Boolean(event.detail?.variant));
    const button = event.target.querySelector(collectionButtonSelector);
    if (!button) return;
    scheduleButtonStateRefresh(button);
  });
  document.addEventListener("quick-buy:variant-change", (event) => {
    const card = event.target.closest("product-item");
    if (!card) return;

    card.dataset.swymVariantValid = String(Boolean(event.detail?.variantOption));
    const button = card.querySelector(collectionButtonSelector);
    if (!button) return;
    scheduleButtonStateRefresh(button);
  });
  document.addEventListener("variant:change", (event) => {
    const productForm = event.target.closest("product-form");
    if (!productForm) return;

    productForm.dataset.swymVariantValid = String(productFormHasMatchingVariant(productForm));
    productForm.querySelectorAll(productButtonSelector).forEach((button) => {
      scheduleButtonStateRefresh(button);
    });
  });
  document.addEventListener("click", handleClick, true);
})();
